/**
 * Android AdMob policy:
 * - Free users: banner only on Hub.
 * - Interstitial only at natural returns to Hub, with cooldown + daily cap.
 * - Rewarded is optional: grants a 30-minute ad-free break.
 * - Premium users: no ads.
 */
import { Capacitor } from "@capacitor/core";

export const AD_UNITS = {
  banner: "ca-app-pub-9771704235649333/7136820182",
  interstitial: "ca-app-pub-9771704235649333/3748239510",
  rewarded: "ca-app-pub-9771704235649333/2470248647",
};

export const ADMOB_TESTING = false;

/**
 * Kill-switch for Play Store screenshots / review.
 * `false` = ads on for free Android users (premium still ad-free).
 */
export const ADS_TEMPORARILY_DISABLED = false;

const INTERSTITIAL_COOLDOWN_MS = 4 * 60 * 1000;
const INTERSTITIAL_DAILY_CAP = 6;
const REWARDED_AD_FREE_MS = 30 * 60 * 1000;
const TEMP_AD_FREE_KEY = "pianoTutorAdFreeUntil";
const DAILY_KEY = "pianoTutorInterstitialDaily";

let initialized = false;
let bannerShowing = false;
let premium = false;
let onHub = false;
let naturalBreaks = 0;
const sessionStartedAt = Date.now();

export function isNativeAndroid(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

/** True when AdMob surfaces may show (Android free users, ads not paused). */
export function areAdsEnabled(): boolean {
  return !ADS_TEMPORARILY_DISABLED && isNativeAndroid();
}

function temporaryAdFreeUntil(): number {
  try {
    return Number(localStorage.getItem(TEMP_AD_FREE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function adsAllowed(): boolean {
  if (ADS_TEMPORARILY_DISABLED) return false;
  return isNativeAndroid() && !premium && Date.now() >= temporaryAdFreeUntil();
}

type DailyCount = { date: string; count: number; lastAt: number };

function readDaily(): DailyCount {
  const date = new Date().toISOString().slice(0, 10);
  try {
    const saved = JSON.parse(localStorage.getItem(DAILY_KEY) || "{}") as Partial<DailyCount>;
    if (saved.date === date) {
      return { date, count: Number(saved.count) || 0, lastAt: Number(saved.lastAt) || 0 };
    }
  } catch {
    /* */
  }
  return { date, count: 0, lastAt: 0 };
}

function writeDaily(value: DailyCount): void {
  try {
    localStorage.setItem(DAILY_KEY, JSON.stringify(value));
  } catch {
    /* */
  }
}

export async function initAds(): Promise<void> {
  if (!isNativeAndroid() || initialized) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.initialize({ initializeForTesting: ADMOB_TESTING });
    initialized = true;
  } catch (e) {
    console.warn("[ads] init failed", e);
  }
}

export async function showBannerAd(): Promise<void> {
  if (!adsAllowed() || !onHub || bannerShowing) return;
  await initAds();
  try {
    const { AdMob, BannerAdSize, BannerAdPosition } = await import(
      "@capacitor-community/admob"
    );
    await AdMob.showBanner({
      adId: AD_UNITS.banner,
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
      isTesting: ADMOB_TESTING,
    });
    bannerShowing = true;
    document.body.classList.add("has-ad-banner");
  } catch (e) {
    console.warn("[ads] banner failed", e);
  }
}

export async function hideBannerAd(): Promise<void> {
  if (!isNativeAndroid() || !bannerShowing) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.hideBanner();
  } catch {
    /* */
  } finally {
    bannerShowing = false;
    document.body.classList.remove("has-ad-banner");
  }
}

export function configureAdsForAccount(isPremium: boolean): void {
  premium = isPremium;
  if (!adsAllowed()) void hideBannerAd();
  else if (onHub) void showBannerAd();
}

export function setAdSurface(surface: "hub" | "content"): void {
  onHub = surface === "hub";
  if (onHub && adsAllowed()) void showBannerAd();
  else void hideBannerAd();
}

/** Natural-break interstitial: every second eligible return, max 6/day. */
export async function maybeShowNaturalInterstitial(): Promise<void> {
  naturalBreaks += 1;
  if (!adsAllowed() || !onHub) return;
  if (Date.now() - sessionStartedAt < 3 * 60 * 1000 || naturalBreaks % 2 !== 0) return;

  const daily = readDaily();
  if (
    daily.count >= INTERSTITIAL_DAILY_CAP ||
    Date.now() - daily.lastAt < INTERSTITIAL_COOLDOWN_MS
  ) {
    return;
  }

  await initAds();
  await hideBannerAd();
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareInterstitial({
      adId: AD_UNITS.interstitial,
      isTesting: ADMOB_TESTING,
    });
    await AdMob.showInterstitial();
    writeDaily({ ...daily, count: daily.count + 1, lastAt: Date.now() });
  } catch (e) {
    console.warn("[ads] interstitial failed", e);
  } finally {
    if (onHub && adsAllowed()) void showBannerAd();
  }
}

/** Optional reward: user watches one ad for 30 minutes without any ads. */
export async function showRewardedAdFreeBreak(): Promise<boolean> {
  if (!adsAllowed()) return false;
  await initAds();
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareRewardVideoAd({
      adId: AD_UNITS.rewarded,
      isTesting: ADMOB_TESTING,
    });
    const reward = await AdMob.showRewardVideoAd();
    if (!reward) return false;
    localStorage.setItem(TEMP_AD_FREE_KEY, String(Date.now() + REWARDED_AD_FREE_MS));
    await hideBannerAd();
    return true;
  } catch (e) {
    console.warn("[ads] rewarded failed", e);
    return false;
  }
}


